import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";

type UseFilterDropdownOptions = {
  id: string;
  onRestore: () => void;
};

type CloseOptions = {
  restore?: boolean;
};

type CloseFn = (options?: CloseOptions) => void;

const filterRegistry = new Map<string, CloseFn>();
let activePinnedFilterId: string | null = null;

function registerFilter(id: string, closeFn: CloseFn) {
  filterRegistry.set(id, closeFn);
}

function unregisterFilter(id: string) {
  filterRegistry.delete(id);
  if (activePinnedFilterId === id) {
    activePinnedFilterId = null;
  }
}

function closeOtherFilters(currentId: string) {
  filterRegistry.forEach((closeFn, id) => {
    if (id !== currentId) {
      closeFn();
    }
  });
}

function setActivePinnedFilter(id: string | null) {
  activePinnedFilterId = id;
}

function otherFilterPinned(currentId: string) {
  return activePinnedFilterId !== null && activePinnedFilterId !== currentId;
}

export default function useFilterDropdown({
  id,
  onRestore,
}: UseFilterDropdownOptions) {
  const [isPinned, setIsPinned] = useState(false);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const hoverOpenRef = useRef(isHoverOpen);

  useEffect(() => {
    hoverOpenRef.current = isHoverOpen;
  }, [isHoverOpen]);

  const close = useCallback(
    (options?: CloseOptions) => {
      if (options?.restore !== false) {
        onRestore();
      }
      setIsPinned(false);
      hoverOpenRef.current = false;
      setIsHoverOpen(false);
      if (activePinnedFilterId === id) {
        setActivePinnedFilter(null);
      }
    },
    [id, onRestore],
  );

  useEffect(() => {
    registerFilter(id, close);
    return () => {
      unregisterFilter(id);
    };
  }, [id, close]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen && otherFilterPinned(id)) {
        return;
      }

      hoverOpenRef.current = nextOpen;
      setIsHoverOpen(nextOpen);

      if (!nextOpen) {
        if (isPinned) {
          return;
        }

        onRestore();
      }
    },
    [id, isPinned, onRestore],
  );

  const handleTriggerClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const nextPinned = !isPinned;
      setIsPinned(nextPinned);

      if (nextPinned) {
        closeOtherFilters(id);
        hoverOpenRef.current = true;
        setIsHoverOpen(true);
        setActivePinnedFilter(id);
        return;
      }

      if (activePinnedFilterId === id) {
        setActivePinnedFilter(null);
      }
      if (!hoverOpenRef.current) {
        onRestore();
      }
      setIsHoverOpen(hoverOpenRef.current);
    },
    [id, isPinned, onRestore],
  );

  const handleInteractOutside = useCallback(() => {
    close();
  }, [close]);

  return {
    open: isPinned || isHoverOpen,
    isPinned,
    handleOpenChange,
    handleTriggerClick,
    handleInteractOutside,
    close,
  };
}
